/**
 * Browser-side `track()` helper. Single entry point for all client-side
 * analytics. Mirrors writes to:
 *
 *   1. PostHog (rich product analytics, our source of truth for the agents)
 *   2. GTM dataLayer (legacy Google Tag Manager pipeline)
 *
 * Server-side mirroring to the Postgres `UserEvent` table is handled by
 * `useTrack` via tRPC.
 *
 * Event names are SnakeCase strings (e.g. `sign_up`, `signup_completed`,
 * `subscription_started`, `paywall_shown`). They map 1:1 to entries in the
 * Prisma `UserEventType` enum (lowercased) so dashboards can pivot on either store.
 */
import { browserCapture } from '@plura/shared/posthog';
import type { GrowthStage } from './eventTypes';

export type TrackProperties = Record<
  string,
  string | number | boolean | null | undefined | string[] | number[]
>;

export interface TrackOptions {
  /// Workspace context (becomes a top-level property + group on PostHog).
  workspaceId?: string;
  /// AARRR stage attribution. Auto-derived from event name if omitted.
  stage?: GrowthStage;
  /// Skip the GTM dataLayer push (e.g. for high-volume events that already
  /// flood GTM).
  skipDataLayer?: boolean;
}

/**
 * Routes excluded from analytics tracking. Operator/internal surfaces should
 * not pollute funnels, retention, session replay, or the `UserEvent` table.
 * Mirror this list in `@plura/shared/posthog/lib/client.ts` if you change it.
 */
const EXCLUDED_PATH_PREFIXES = ['/admin'];

export function isTrackingExcludedPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return EXCLUDED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Capture a client-side analytics event.
 */
export function track(
  event: string,
  properties: TrackProperties = {},
  options: TrackOptions = {}
): void {
  if (isTrackingExcludedPath()) return;

  const enrichedProps = {
    ...properties,
    ...(options.workspaceId ? { workspace_id: options.workspaceId } : {}),
    ...(options.stage ? { stage: options.stage } : {}),
  };

  if (typeof window !== 'undefined' && !options.skipDataLayer) {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ event, ...enrichedProps });
  }

  browserCapture(event, enrichedProps, {
    distinctId: '',
    workspaceId: options.workspaceId,
    stage: options.stage,
  });
}

/**
 * Identify the current user to PostHog (and any downstream tools). Call this
 * once on session load and again whenever onboarding properties change.
 */
export { browserIdentify as identify, browserReset as resetIdentity, initBrowserPostHog } from '@plura/shared/posthog';

// ─── Backwards-compatible thin wrappers ──────────────────────────────────────
// Existing call sites invoke `events.signup(...)` / `events.formCreated(...)`.
// Rather than break every consumer, we keep these as one-line wrappers that
// forward to `track()` with the new event name. New code should use `track()`
// directly.

export function signup(userId: string, data: { email: string; name: string }) {
  // `sign_up` is the GTM / Google Ads conversion trigger name.
  track('sign_up', { user_id: userId, ...data }, { stage: 'ACQUISITION' });
  track('signup_completed', { user_id: userId, ...data }, { stage: 'ACQUISITION' });
}

export function openBillingModal() {
  track('open_billing_modal', {}, { stage: 'REVENUE' });
}

export function subscriptionStarted() {
  track('subscription_started', {}, { stage: 'REVENUE' });
}

export function subscriptionCancelled() {
  track('subscription_cancelled', {}, { stage: 'REVENUE' });
}

export function emailVerified() {
  track('email_verified', {}, { stage: 'ACTIVATION' });
}

export function resetPasswordRequested() {
  track('reset_password_requested', {}, { stage: 'ACTIVATION' });
}

export function resetPasswordSuccess() {
  track('reset_password_success', {}, { stage: 'ACTIVATION' });
}

export function workspaceInviteAccepted() {
  track('workspace_invite_accepted', {}, { stage: 'ACTIVATION' });
}

export function formCreated(collectionId: string, template?: string) {
  track('form_created', { collection_id: collectionId, template }, { stage: 'ACTIVATION' });
}

export function formDraftSaved(collectionId: string) {
  track('form_draft_saved', { collection_id: collectionId }, { stage: 'ACTIVATION' });
}

export function formPublished(collectionId: string) {
  track('form_published', { collection_id: collectionId }, { stage: 'ACTIVATION' });
}

export function addJourneyItem(type: string) {
  track('add_journey_item', { type }, { stage: 'ACTIVATION' });
}

export function editJourneyItem() {
  track('edit_journey_item', {}, { stage: 'ACTIVATION' });
}

export function openShareFormModal(collectionId: string) {
  track('open_share_form_modal', { collection_id: collectionId }, { stage: 'ACTIVATION' });
}

/**
 * Public live form load (`/l/[collectionId]`). Anonymous respondents — PostHog
 * only (not `UserEvent`). Used to debug session funnels vs FormView counts.
 */
export function publicFormOpened(input: {
  collectionId: string;
  workspaceId: string;
  formId: string;
}) {
  track(
    'public_form_opened',
    {
      collection_id: input.collectionId,
      form_id: input.formId,
    },
    { workspaceId: input.workspaceId, stage: 'ACTIVATION' }
  );
}

export function createContact(input: { workspaceId: string; contactId: string }) {
  track(
    'create_contact',
    { contact_id: input.contactId },
    { workspaceId: input.workspaceId, stage: 'ACTIVATION' }
  );
}

export function createConversation(input: {
  collectionId: string;
  contactId: string;
  conversationId: string;
}) {
  track(
    'create_conversation',
    {
      collection_id: input.collectionId,
      contact_id: input.contactId,
      conversation_id: input.conversationId,
    },
    { stage: 'ACTIVATION' }
  );
}

export function finishedConversation(input: {
  collectionId: string;
  contactId: string;
  conversationId: string;
}) {
  track(
    'finished_conversation',
    {
      collection_id: input.collectionId,
      contact_id: input.contactId,
      conversation_id: input.conversationId,
    },
    { stage: 'ACTIVATION' }
  );
}

/**
 * UI reached a terminal step (end / redirect / synthetic exit / last welcome).
 * Fires even when `summarizeConversation` never runs — so PostHog can show
 * "saw thank-you" without a COMPLETED row. Never includes answer text (PII).
 */
export function formEndReached(input: {
  collectionId: string;
  conversationId?: string;
  via: 'end_screen' | 'redirect_screen' | 'exit' | 'welcome_terminal';
  stepType?: string;
}) {
  track(
    'form_end_reached',
    {
      collection_id: input.collectionId,
      conversation_id: input.conversationId,
      via: input.via,
      step_type: input.stepType,
    },
    { stage: 'ACTIVATION' }
  );
}

export function answerQuestion(input: {
  collectionId: string;
  journeyItemId: string;
  conversationId?: string;
  stepType?: string;
}) {
  track(
    'answer_question',
    {
      collection_id: input.collectionId,
      journey_item_id: input.journeyItemId,
      conversation_id: input.conversationId,
      step_type: input.stepType,
    },
    { stage: 'ACTIVATION' }
  );
}

export function openAiAssistantModal() {
  track('open_ai_assistant_modal', {}, { stage: 'ACTIVATION' });
}

export function signUpFormOpened() {
  track('sign_up_form_opened', {}, { stage: 'ACQUISITION' });
}

export function createFormWithAiFormOpened() {
  track('create_form_with_ai_opened', {}, { stage: 'ACTIVATION' });
}

export function createFormWithAiFormSubmitted(prompt: string) {
  track('create_form_with_ai_submitted', { prompt }, { stage: 'ACTIVATION' });
}

export function createFormFromTypeform() {
  track('create_form_from_typeform', {}, { stage: 'ACTIVATION' });
}

export function addTypeformToWorkspace() {
  track('add_typeform_to_workspace', {}, { stage: 'ACTIVATION' });
}

export function signupFromTypeformImporter() {
  track('signup_from_typeform_importer', {}, { stage: 'ACQUISITION' });
}

export function addTemplateToWorkspace(templateId: string) {
  track('add_template_to_workspace', { template_id: templateId }, { stage: 'ACTIVATION' });
}

export function previewTemplate(templateId: string) {
  track('preview_template', { template_id: templateId }, { stage: 'ACTIVATION' });
}

export function signUpWithTemplate(templateId: string) {
  track('sign_up_with_template', { template_id: templateId }, { stage: 'ACQUISITION' });
}

export function howToTooltipOpened(tooltipName: string) {
  track('how_to_tooltip_opened', { tooltip_name: tooltipName }, { stage: 'ACTIVATION' });
}

export function howToTooltipVideoViewed(tooltipName: string) {
  track('how_to_tooltip_video_viewed', { tooltip_name: tooltipName }, { stage: 'ACTIVATION' });
}

export function editorGetStartedIntro() {
  track('editor_get_started_intro', {}, { stage: 'ACTIVATION' });
}

export function editorGetStartedIntroSkipped() {
  track('editor_get_started_intro_skipped', {}, { stage: 'ACTIVATION' });
}

export function editorGetStartedIntroVideoViewed(video: string) {
  track('editor_get_started_intro_video_viewed', { video }, { stage: 'ACTIVATION' });
}

export function aiChatStarted(collectionId: string) {
  track('ai_chat_started', { collection_id: collectionId }, { stage: 'RETENTION' });
}

export function aiChatChangesApplied(collectionId: string, changesCount: number) {
  track(
    'ai_chat_changes_applied',
    { collection_id: collectionId, changes_count: changesCount },
    { stage: 'RETENTION' }
  );
}

export function aiChatChangesDiscarded(
  collectionId: string,
  changesCount: number,
  chatLength: number
) {
  track(
    'ai_chat_changes_discarded',
    {
      collection_id: collectionId,
      changes_count: changesCount,
      chat_length: chatLength,
    },
    { stage: 'RETENTION' }
  );
}

export function aiChatLimitReached(collectionId: string) {
  track('ai_chat_limit_reached', { collection_id: collectionId }, { stage: 'REVENUE' });
}

export function aiChatUpgradeClicked(collectionId: string) {
  track('ai_chat_upgrade_clicked', { collection_id: collectionId }, { stage: 'REVENUE' });
}

export function builtWithBadgeClicked(input: { workspaceId: string; collectionId: string }) {
  track(
    'built_with_badge_clicked',
    { collection_id: input.collectionId },
    { workspaceId: input.workspaceId, stage: 'REFERRAL' }
  );
}
